import styles from './export-history-drawer.module.scss';
import { ExportHistoryResponse } from '@/services/call-log-service/call-log-service.types';
import { calculateDateFromISOtoYYYY_MM_DD, capitalizeFirstLetter } from '@/utils/helpers';
import { Divider, Drawer, Flex, message, Pagination } from 'antd';
import { Button } from '@/components/button/button';
import Trash from '@/assets/icons/trash-01.svg?react';
import Icon from '@ant-design/icons';
import { useDeleteExportHistoryPendingFile } from '@/hooks/use-delete-export-history-pending-file/use-delete-export-history-pending-file';
import { useEffect, useState } from 'react';
import { NoRecords } from '../no-records/no-records';
import { Spin } from 'antd';
import { useScreenSize } from '@/hooks/use-screen-size/use-screen-size';
import { ExportButton } from '../export-button/export-button';
import { InfoComponent } from '../info-component/info-component';

interface ExportHistoryDrawerProps {
  isVisible: boolean;
  onClose: () => void;
  className?: string;
  exportHistoryData?: ExportHistoryResponse[];
  isLoadingData: boolean;
  onExportClick: (id: number) => void;
  onPrepareExport: () => void;
  isDownloading: { [key: number]: boolean };
  isExporting: boolean;
  currentPage: number;
  perPage: number;
  totalExports: number;
  onChangePagination: ((page: number, pageSize: number) => void) | undefined;
  isEmptyData: boolean;
}

export function ExportHistoryDrawer({
  isVisible,
  onClose,
  onExportClick,
  onPrepareExport,
  exportHistoryData,
  isLoadingData,
  isDownloading,
  isExporting,
  currentPage,
  perPage,
  totalExports,
  onChangePagination,
  isEmptyData,
}: ExportHistoryDrawerProps) {
  const { deleteExportHistory } = useDeleteExportHistoryPendingFile();
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const screenSize = useScreenSize();
  const [messageApi, contextHolder] = message.useMessage();

  const [exportTimestamps, setExportTimestamps] = useState<number[]>(() => {
    return JSON.parse(localStorage.getItem('exportTimestamps') || '[]');
  });
  const [cooldown, setCooldown] = useState<boolean>(() => {
    return localStorage.getItem('cooldown') === 'true';
  });

  const isMobile = screenSize.width < 768;

  const handleDelete = (id: number) => {
    setDeletingId(id);
    deleteExportHistory(id, {
      onSettled: () => {
        setDeletingId(null);
      },
    });
  };

  useEffect(() => {
    if (cooldown) {
      const timer = setTimeout(() => {
        setCooldown(false);
        localStorage.removeItem('cooldown');
      }, 120000);

      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  useEffect(() => {
    localStorage.setItem('exportTimestamps', JSON.stringify(exportTimestamps));
    localStorage.setItem('cooldown', cooldown.toString());
  }, [exportTimestamps, cooldown]);

  const handlePrepareExport = () => {
    const currentTimestamp = Date.now();
    const validTimestamps = exportTimestamps.filter(
      (timestamp) => currentTimestamp - timestamp < 120000
    );

    if (validTimestamps.length >= 4) {
      setCooldown(true);
      messageApi.warning('You have reached the export limit. Cooldown started.');
      return;
    }

    setExportTimestamps([...validTimestamps, currentTimestamp]);
    onPrepareExport();
  };

  return (
    <Drawer
      placement="right"
      onClose={onClose}
      open={isVisible}
      width={500}
      className={styles.drawerExportHistory}
    >
      {contextHolder}
      {isLoadingData ? (
        <Spin className={styles.spinner} size="large" />
      ) : exportHistoryData?.length ? (
        <Flex vertical>
          <ExportButton
            isExporting={isExporting}
            onPrepareExport={handlePrepareExport}
            isMobile={isMobile}
            isEmptyData={isEmptyData}
            isDisabled={cooldown}
          />
          {cooldown && (
            <div style={{ paddingTop: 12 }}>
              <InfoComponent
                type="warning"
                text="You have reached the limit of 4 exports. Please wait 2 minutes."
              />
            </div>
          )}
          <Divider />

          <div className={styles.titleContainer}>
            <p className={styles.titleExport}>Export History</p>
            <p className={styles.titleStatus}>Status</p>
          </div>

          {exportHistoryData?.map((data) => (
            <div key={data.id} className={styles.dataContainer}>
              <Button
                loading={isDownloading[data.id] || false}
                onClick={() => onExportClick(data.id)}
                disabled={data.status === 'pending'}
                style={{ fontSize: 13, fontWeight: 400, color: styles.buttonColor }}
                buttonVariant="tertiary"
              >
                {data.name}
              </Button>
              <Flex>
                <Flex vertical style={{ marginRight: data.status === 'complete' ? '20px' : '0px' }}>
                  <p
                    className={`${styles.status} ${data.status === 'complete' ? styles.statusComplete : styles.statusPending}`}
                  >
                    {capitalizeFirstLetter(data.status)}
                  </p>
                  <p className={styles.date}>{calculateDateFromISOtoYYYY_MM_DD(data.created_at)}</p>
                </Flex>
                <Flex style={{ width: '20px' }}>
                  {data.status === 'pending' && (
                    <Button
                      className={styles.deleteIcon}
                      onClick={() => handleDelete(data.id)}
                      loading={deletingId === data.id}
                      buttonVariant="tertiary"
                    >
                      <Icon component={() => <Trash width="14" height="14" />} />
                    </Button>
                  )}
                </Flex>
              </Flex>
            </div>
          ))}
        </Flex>
      ) : (
        <>
          <ExportButton
            isExporting={isExporting}
            onPrepareExport={onPrepareExport}
            isMobile={isMobile}
            isEmptyData={isEmptyData}
          />
          <Divider />

          <NoRecords
            className={styles.noRecords}
            description="There are no exported history files to display"
          />
        </>
      )}

      {isLoadingData ? (
        <Spin className={styles.paginationSpin} spinning={isLoadingData} size="small" />
      ) : (
        <Pagination
          className={styles.pagination}
          current={currentPage}
          pageSize={perPage}
          total={totalExports}
          onChange={onChangePagination}
        />
      )}
    </Drawer>
  );
}
